import { useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { AuthProvider } from '@/contexts/AuthContext'
import I18nProvider from '@/components/providers/I18nProvider'
import UniversityHeader from '@/components/layout/UniversityHeader'
import UniversityFooter from '@/components/layout/UniversityFooter'

import LandingPage from '@/pages/LandingPage'
import EnrolPage from '@/pages/EnrolPage'
import DashboardPage from '@/pages/DashboardPage'
import DegreePage from '@/pages/DegreePage'
import ModulePage from '@/pages/ModulePage'
import SessionPage from '@/pages/SessionPage'
import ProgressPage from '@/pages/ProgressPage'
import CertificatesPage from '@/pages/CertificatesPage'
import CorporatePage from '@/pages/CorporatePage'
import CorporateAdminPage from '@/pages/CorporateAdminPage'
import SignInPage from '@/pages/SignInPage'
import SignUpPage from '@/pages/SignUpPage'
import GiftPage from '@/pages/GiftPage'
import RedeemPage from '@/pages/RedeemPage'
import GivePage from '@/pages/GivePage'
import NFPPage from '@/pages/NFPPage'
import NGODashboardPage from '@/pages/NGODashboardPage'
import TermsPage from '@/pages/TermsPage'
import PrivacyPage from '@/pages/PrivacyPage'
import PrepPage from '@/pages/PrepPage'
import ActivatePage from '@/pages/ActivatePage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import SettingsPage from '@/pages/SettingsPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import VerifyPage from '@/pages/VerifyPage'
import ScrollToTop from '@/components/common/ScrollToTop'

const M = '#0d0d1a'; const G = '#d4a843'; const IN = '#0ea5e9'
const MU = 'rgba(255,255,255,0.45)'

function GiftSentPage() {
  return (
    <div style={{ background: '#06060c', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif", color: '#fff' }}>
      <div style={{ textAlign: 'center', maxWidth: 500, padding: '40px 24px' }}>
        <div style={{ fontSize: 72, marginBottom: 24 }}>🎓</div>
        <h1 style={{ fontSize: 30, fontWeight: 900, margin: '0 0 12px', fontFamily: "'Playfair Display', Georgia, serif" }}>Gift Sent!</h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15, lineHeight: 1.7, margin: '0 0 12px' }}>
          Your gift of education is on its way. The recipient will receive an email with their gift code and instructions to claim it.
        </p>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginBottom: 28 }}>They have 12 months to redeem the code.</p>
        <a href="/gift" style={{ display: 'inline-block', background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', borderRadius: 12, padding: '14px 28px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
          Gift Another Degree →
        </a>
      </div>
    </div>
  )
}

function GiveThankyouPage() {
  return (
    <div style={{ background: '#06060c', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif", color: '#fff' }}>
      <div style={{ textAlign: 'center', maxWidth: 520, padding: '40px 24px' }}>
        <div style={{ fontSize: 72, marginBottom: 24 }}>💙</div>
        <h1 style={{ fontSize: 30, fontWeight: 900, margin: '0 0 12px', fontFamily: "'Playfair Display', Georgia, serif" }}>Thank You</h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15, lineHeight: 1.7, margin: '0 0 12px' }}>
          Your contribution goes directly into the Still Waters scholarship fund. We'll send you a receipt by email.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, lineHeight: 1.65, margin: '0 0 32px' }}>
          When your contribution funds someone's education, we'll let you know. You've done something that actually matters.
        </p>
        <a href="/give" style={{ display: 'inline-block', background: `linear-gradient(135deg, ${IN}, #0284c7)`, color: '#fff', borderRadius: 12, padding: '14px 28px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
          Give Again →
        </a>
      </div>
    </div>
  )
}

function needsFullScreen(path: string) {
  return path.startsWith('/learn/')
}

function AppShell() {
  const location = useLocation()
  const fullScreen = needsFullScreen(location.pathname)

  // Capture affiliate referral code
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const ref = searchParams.get('ref')
    if (ref) {
      localStorage.setItem('sage_affiliate_ref', ref.toUpperCase())
      // Clean URL: Remove ref for a cleaner experience
      const url = new URL(window.location.href)
      url.searchParams.delete('ref')
      window.history.replaceState({}, '', url.toString())
    }
  }, [location.search])

  return (
    <div style={{ minHeight: '100dvh', width: '100%', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
      <ScrollToTop />
      {!fullScreen && <UniversityHeader />}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/enrol" element={<EnrolPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/degree/:classId" element={<DegreePage />} />
          <Route path="/module/:classId/:moduleId" element={<ModulePage />} />
          <Route path="/learn/:classId" element={<SessionPage />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/certificates" element={<CertificatesPage />} />
          <Route path="/corporate" element={<CorporatePage />} />
          <Route path="/corporate/admin" element={<CorporateAdminPage />} />
          <Route path="/corporate-admin" element={<CorporateAdminPage />} />
          <Route path="/gift" element={<GiftPage />} />
          <Route path="/gift/sent" element={<GiftSentPage />} />
          <Route path="/redeem" element={<RedeemPage />} />
          <Route path="/redeem/:code" element={<RedeemPage />} />
          <Route path="/give" element={<GivePage />} />
          <Route path="/give/thankyou" element={<GiveThankyouPage />} />
          <Route path="/nfp" element={<NFPPage />} />
          <Route path="/ngo/admin" element={<NGODashboardPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/prep" element={<PrepPage />} />
          <Route path="/prep/enrol/:slug" element={<PrepPage />} />
          <Route path="/prep/start/:slug" element={<PrepPage />} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/activate/:code" element={<ActivatePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify/:code" element={<VerifyPage />} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </main>
      {!fullScreen && <UniversityFooter />}
    </div>
  )
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </I18nProvider>
  )
}
